import {
  type DatadogRum,
  datadogRum,
  type StartRecordingOptions,
  type User,
} from '@datadog/browser-rum';
import {once} from 'lodash';
import {useEffect} from 'react';

import {envIsPublicCloud} from '../../config';
import {
  hasViewEverBeenBackgrounded,
  onViewBackgrounded,
  resetViewBackgrounded,
} from '../viewVisibility';
import {
  ALLOWED_TRACING_URLS,
  DATADOG_CLIENT_TOKEN,
  DATADOG_RUM_APPLICATION_ID,
  DATADOG_SITE,
  DATADOG_UI_SERVICE,
} from './constants';
import {
  shouldDiscardRumEvent,
  shouldExcludeUrlFromPageActivity,
} from './datadogRumDiscardEvent';
import {redactRumContext} from './redactRumContext';
import {
  redactGraphQlSensitiveFields,
  redactRumEventUrls,
} from './redactSensitiveFields';

const rumFacade = Object.create(datadogRum);

rumFacade.addDurationVital = skipFnIfHidden(datadogRum.addDurationVital);
rumFacade.addTiming = skipFnIfHidden(datadogRum.addTiming);
rumFacade.addAction = skipFnIfHidden(datadogRum.addAction);

/**
 * Returns a facade over the Datadog RUM global that skips certain tracking calls when
 * the window has been backgrounded, avoiding metrics that are inaccurate while the page
 * is hidden.
 *
 * @returns The Datadog RUM facade with view visibility checks
 * @deprecated Please do not access RUM object directly. Opt for a wrapper util instead
 */
export function getDatadogRum(): DatadogRum {
  return rumFacade;
}

/**
 * Which RUM mechanism to reach for (view/global context are documented on their
 * wrapper functions below):
 * - Actions (`recordRumAction` below): discrete, user-initiated interactions
 *   (clicks, submits) — not timing data or passive/derived metadata. Names governed by
 *   the action-name allowlist.
 * - Duration vitals (`recordRumDurationVital` below): how long an operation took
 *   (e.g. workspace save). Vitals inherit the current view's context, so prefer these
 *   over stuffing durations into an action payload. `startTime` is an absolute epoch
 *   timestamp (`performance.timeOrigin + <relative perf.now() mark>`).
 */

/**
 * Records a custom RUM action. The only sanctioned entry point for custom actions
 * (enforced by `wandb/dd-action-use-wrapper`), keeping call sites consistent and
 * leaving one place to evolve behavior (name validation, sampling, redaction, ...)
 * later. Routes through the facade, so actions are skipped when the view has been
 * backgrounded.
 *
 * @param name - the RUM action name
 * @param context - optional per-event context attached to the action
 */
export function recordRumAction(
  name: string,
  context?: Parameters<DatadogRum['addAction']>[1]
): void {
  getDatadogRum().addAction(name, context);
}

/**
 * Records a duration vital retrospectively: measure with `performance.now()` marks,
 * then pass an epoch-anchored `startTime` (`performance.timeOrigin + <mark>`) and the
 * measured `duration`. The only sanctioned duration-vital entry point (enforced by
 * `wandb/dd-vital-use-wrapper`). `startDurationVital`/`stopDurationVital` are
 * disallowed: backgrounding the view between start and stop skews the recorded
 * duration.
 *
 * @param name - the RUM vital name, should be unique
 * @param options - startTime/duration plus optional description and context
 */
export function recordRumDurationVital(
  name: string,
  options: Parameters<DatadogRum['addDurationVital']>[1]
): void {
  getDatadogRum().addDurationVital(name, options);
}

/**
 * Records a handled error to Datadog RUM. The only sanctioned entry point for
 * manual error reports (enforced by `wandb/dd-error-use-wrapper`) — do not call
 * `datadogRum.addError` directly. Errors are intentionally not skipped when the
 * view is backgrounded (unlike actions and timings): an error is meaningful
 * whenever it happens.
 *
 * @param error - the error to report
 * @param context - optional per-event context attached to the error
 */
export function recordRumError(
  error: unknown,
  context?: Parameters<DatadogRum['addError']>[1]
): void {
  getDatadogRum().addError(error, context);
}

/**
 * Sets the RUM user, stamped on every event as `@usr`. The only sanctioned entry
 * point for user identification (enforced by `wandb/dd-user-use-wrapper`).
 * Requires an `id`: the overload of the raw `setUser` that accepts an id-less
 * user is deprecated by the SDK.
 *
 * @param user - user info; `id` is required
 */
export function setRumUser(user: User & {id: string}): void {
  getDatadogRum().setUser(user);
}

/**
 * Starts Session Replay recording. The only sanctioned entry point for
 * controlling replay recording (enforced by `wandb/dd-user-use-wrapper`).
 *
 * Note: with `sessionReplaySampleRate` set and no
 * `startSessionReplayRecordingManually: true` in init, the SDK (v5+) records
 * sampled sessions automatically and this call is a no-op safety net.
 *
 * @param options - e.g. `{force: true}` to record a session sampled out of replay
 */
export function startRumSessionReplayRecording(
  options?: StartRecordingOptions
): void {
  getDatadogRum().startSessionReplayRecording(options);
}

/**
 * Sets a view-context property: a stable, low-cardinality dimension scoped to the
 * current view, stamped on every event for that view and reset when a new view starts.
 * Prefer this (and the `use*RumViewContext` hooks) over `datadogRum.setViewContextProperty`
 * directly; `key` must be allowlisted in `datadogContextKeys.json` (enforced by
 * `wandb/dd-context-property-allowlist`), keeping dashboard keys stable.
 *
 * @param key - allowlisted context property key
 * @param value - context value
 */
export function setRumViewContextProperty(key: string, value: unknown): void {
  getDatadogRum().setViewContextProperty(key, value);
}

/**
 * Sets a global/session context property: a session-wide attribute spanning all views,
 * stamped on every event regardless of route. Prefer view context when the value can
 * change as the user navigates; same `datadogContextKeys.json` allowlist applies.
 *
 * @param key - allowlisted context property key
 * @param value - context value
 */
export function setRumGlobalContextProperty(key: string, value: unknown): void {
  getDatadogRum().setGlobalContextProperty(key, value);
}

/**
 * Removes a session/global context key. No view-context equivalent exists — view
 * context is cleared automatically on view change.
 *
 * @param key - allowlisted context property key to remove
 */
export function removeRumGlobalContextProperty(key: string): void {
  getDatadogRum().removeGlobalContextProperty(key);
}

/**
 * Sets a global/session context property (session-wide, spans all views) for the
 * hook's lifetime; unset when the property changes or the component unmounts. Prefer
 * view context when the value can change as the user navigates.
 *
 * @param key
 * @param value
 */
export function useRumGlobalContext(
  key: string,
  value: unknown,
  shouldLog: boolean = true
) {
  useEffect(() => {
    if (shouldLog) {
      setRumGlobalContextProperty(key, value);
      return () => {
        removeRumGlobalContextProperty(key);
      };
    }
    return undefined;
  }, [key, value, shouldLog]);
}

/**
 * As with `useRumGlobalContext` (session-wide attribute spanning all views; prefer
 * view context for values that change on navigation), but does not unset on unmount.
 * @param key
 * @param value
 * @param shouldLog
 */
export function usePersistentRumGlobalContext(
  key: string,
  value: unknown,
  shouldLog: boolean = true
) {
  useEffect(() => {
    if (shouldLog) {
      setRumGlobalContextProperty(key, value);
    }
  }, [key, value, shouldLog]);
}

/**
 * Sets a view-context property (stable, low-cardinality dimension scoped to the current
 * view; allowlisted in `datadogContextKeys.json`), retained until a new view starts.
 *
 * @param key
 * @param value
 * @param shouldLog
 */
export function usePersistentRumViewContext(
  key: string,
  value: unknown,
  shouldLog: boolean = true
) {
  useEffect(() => {
    if (shouldLog) {
      setRumViewContextProperty(key, value);
    }
  }, [key, value, shouldLog]);
}

const datadogRumViewTimings = new Set<string>();

/**
 * Returns the set of timing names that have been recorded in the current view.
 * This is used to track which timings have already been logged.
 *
 * Lives here (not in `datadogRumTimings.ts`) because it is per-view state owned
 * by the view lifecycle: `startDatadogRumView` resets it, and keeping it in the
 * timings module would create an import cycle with the timing hooks that call
 * `getDatadogRum()`.
 *
 * @returns The set of recorded timing names
 */
export function getDatadogRumViewTimings() {
  return datadogRumViewTimings;
}

/**
 * Starts a new Datadog RUM view with the given name.
 * Clears view-specific timings and resets the view backgrounded state.
 *
 * @param name - The name of the view/route being started
 */
export function startDatadogRumView(name?: string) {
  // See `addFirstTiming` -- clear out the list of already-recorded timings so
  // that they can be re-recorded for the new view.
  getDatadogRumViewTimings().clear();

  const datadogRum = getDatadogRum();
  resetViewBackgrounded();
  // `startView({name})` sets `@view.name`; filter/group RUM by that instead of a
  // separate (session-global) `routeName` context property.
  datadogRum.startView({name});
  // Stamp whether this view started hidden (e.g. opened in a background tab, where
  // browsers throttle timers/rendering); the onViewBackgrounded subscription in
  // `initDatadogRum` flips it to true if the view is backgrounded later. Dashboards
  // filter `@context.datadogView.was_backgrounded:false` to exclude hidden-tab timing
  // artifacts from percentiles.
  setRumViewContextProperty(
    'datadogView.was_backgrounded',
    hasViewEverBeenBackgrounded()
  );
}

/**
 * Initializes Datadog RUM (Real User Monitoring) with configuration for session replay,
 * user tracking, resource monitoring, and custom event filtering.
 * This function is wrapped with `once()` to ensure it only runs once.
 */
export const initDatadogRum = once(function initDatadogRum() {
  datadogRum.init({
    // https://docs.datadoghq.com/real_user_monitoring/browser/monitoring_resource_performance/#track-graphql-requests
    allowedGraphQlUrls: [
      {
        match: /\/graphql$/,
        trackPayload: true, // Include GraphQL query (limited to 32 KB)
      },
    ],
    allowedTracingUrls: ALLOWED_TRACING_URLS,
    // v7 defaults propagateTraceBaggage to true, which sends a `baggage` header
    // on traced cross-origin requests. Keep it off until backend CORS allows the
    // header, to avoid breaking traced API calls.
    propagateTraceBaggage: false,

    applicationId: DATADOG_RUM_APPLICATION_ID,
    beforeSend(event) {
      redactRumEventUrls(event);

      if (shouldDiscardRumEvent(event)) {
        return false;
      }

      if (event.type === 'resource') {
        redactGraphQlSensitiveFields(event);
      }

      // Single sanitization point for every event type: per-call context (actions,
      // vitals, errors) and view/global context all merge into event.context by the
      // time beforeSend runs.
      event.context = redactRumContext({
        ...event.context,
        gitTag: window.CONFIG?.GIT_TAG,
        envIsPublicCloud,
      });

      return true;
    },
    clientToken: DATADOG_CLIENT_TOKEN,
    // SECURITY: Mask all text in session replays to prevent sensitive data exposure.
    // If you want to see an element in session replay, use data-dd-privacy="allow"
    // on specific elements to selectively unmask non-sensitive content.
    defaultPrivacyLevel: 'mask',
    // With defaultPrivacyLevel: 'mask', clicks report as "Masked Element"
    // unless the element (or an ancestor) has a data-dd-action-name or sits
    // inside a data-dd-privacy="allow" container. Set explicitly (v7 default)
    // so on-screen text — which can include user content like run names —
    // never becomes an action name by accident. To surface an interaction in
    // RUM, give it a registered name (see README.md#stable-action-naming).
    enablePrivacyForActionName: true,
    env: window.CONFIG?.ENVIRONMENT_NAME,
    profilingSampleRate: 25,
    service: DATADOG_UI_SERVICE,
    version: window.CONFIG?.GIT_TAG,
    sessionReplaySampleRate: 100,
    site: DATADOG_SITE,
    traceSampleRate: 100,
    // propagate feature flag list to additional even types
    trackFeatureFlagsForEvents: ['vital', 'action', 'long_task', 'resource'],
    trackUserInteractions: true,
    trackLongTasks: true,
    trackResources: true,
    // Only include activity from domains that are in TTI_DOMAIN_INCLUSIONS.
    // This governs the SDK's page-activity / loading-time computation only;
    // resource *collection* is filtered separately in beforeSend via
    // shouldDiscardRumEvent.
    // https://docs.datadoghq.com/real_user_monitoring/application_monitoring/browser/monitoring_page_performance/#how-page-activity-is-calculated
    excludedActivityUrls: [shouldExcludeUrlFromPageActivity],
    trackViewsManually: true,
  });

  // Keep `@context.datadogView.was_backgrounded` (stamped false-or-hidden at each
  // `startDatadogRumView`) truthful for the rest of the view: flip it the first time
  // the tab goes hidden. The `skipFnIfHidden` facade already drops *new* timings once
  // backgrounded, but events recorded before backgrounding — and SDK-computed view
  // metrics like `loading_time` on background-opened tabs — still ship; this property
  // is what lets dashboards filter those out. (`viewVisibility` owns the underlying
  // visibilitychange listener.)
  onViewBackgrounded(() => {
    setRumViewContextProperty('datadogView.was_backgrounded', true);
  });
});

/** Internal functions - please think twice before exporting --------------------------------- */

/**
 * Wraps a Datadog analytics function and returns a new function with the same
 * signature. If the current browser window has ever been backgrounded during
 * the current view, the Datadog function will not be called. This prevents
 * inaccurate metrics from being recorded when the page is not visible.
 * In either case the return value should be the same-ish.
 *
 * @param fn - The Datadog function to wrap
 * @param generateReturnOnHidden - Optional function to generate a return value when the view is hidden
 * @returns A wrapped function that checks view visibility before calling the original
 */
function skipFnIfHidden<Fn extends (...args: any) => any>(
  fn: Fn,
  generateReturnOnHidden?: () => ReturnType<Fn>
) {
  return function (this: any, ...args: Parameters<Fn>): ReturnType<Fn> {
    if (hasViewEverBeenBackgrounded()) {
      if (generateReturnOnHidden) {
        return generateReturnOnHidden();
      }
      return undefined as ReturnType<Fn>;
    }
    return fn.call(this, ...args);
  };
}
